// src/hooks/useSocialLinks.ts
// Hook & service untuk membaca dan menyimpan pengaturan sosial media (YouTube, TikTok, Instagram)

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { globalCache } from './useDataCache';

export interface SocialLinksData {
  youtube: string;
  tiktok: string;
  instagram: string;
}

export const DEFAULT_SOCIAL_LINKS: SocialLinksData = {
  youtube: 'https://www.youtube.com/@evano.rama',
  tiktok: 'https://www.tiktok.com/@evano.rama',
  instagram: 'https://instagram.com/evano.rama',
};

const LOCAL_STORAGE_SOCIAL_KEY = 'mada_social_links_v1';

// Normalisasi input apabila admin hanya mengetik username (misal: @evano.rama atau evano.rama)
export const normalizeSocialUrl = (
  platform: 'youtube' | 'tiktok' | 'instagram',
  value: string
): string => {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  const cleanHandle = trimmed.replace(/^@/, '');
  switch (platform) {
    case 'youtube':
      return `https://www.youtube.com/@${cleanHandle}`;
    case 'tiktok':
      return `https://www.tiktok.com/@${cleanHandle}`;
    case 'instagram':
      return `https://instagram.com/${cleanHandle}`;
  }
};

export const getCachedSocialLinks = (): SocialLinksData => {
  try {
    if (typeof window === 'undefined') return DEFAULT_SOCIAL_LINKS;
    const raw = localStorage.getItem(LOCAL_STORAGE_SOCIAL_KEY);
    if (!raw) return DEFAULT_SOCIAL_LINKS;
    const parsed = JSON.parse(raw);
    return {
      youtube: parsed.youtube ?? DEFAULT_SOCIAL_LINKS.youtube,
      tiktok: parsed.tiktok ?? DEFAULT_SOCIAL_LINKS.tiktok,
      instagram: parsed.instagram ?? DEFAULT_SOCIAL_LINKS.instagram,
    };
  } catch {
    return DEFAULT_SOCIAL_LINKS;
  }
};

export const setCachedSocialLinks = (links: SocialLinksData) => {
  try {
    if (typeof window === 'undefined') return;
    localStorage.setItem(LOCAL_STORAGE_SOCIAL_KEY, JSON.stringify(links));
    window.dispatchEvent(new Event('social-links-updated'));
  } catch (e) {
    console.warn('Could not save social links to localStorage:', e);
  }
};

export const fetchSocialLinksFromDB = async (): Promise<SocialLinksData> => {
  try {
    // 1. Cek dari tabel profiles (kolom contact JSONB)
    const { data: profile } = await supabase
      .from('profiles')
      .select('contact')
      .limit(1)
      .maybeSingle();

    const contactObj = (profile?.contact as Record<string, any>) || {};

    // 2. Cek juga dari tabel contact_info (kolom social_links JSONB)
    const { data: contactInfo } = await supabase
      .from('contact_info')
      .select('social_links')
      .eq('is_active', true)
      .limit(1)
      .maybeSingle();

    const socialObj = (contactInfo?.social_links as Record<string, string>) || {};

    // Cari case-insensitive dari social_links
    const findInSocialObj = (keyName: string): string | undefined => {
      const foundKey = Object.keys(socialObj).find(
        (k) => k.toLowerCase() === keyName.toLowerCase()
      );
      return foundKey ? socialObj[foundKey] : undefined;
    };

    const cached = getCachedSocialLinks();

    const result: SocialLinksData = {
      youtube:
        contactObj.youtube ||
        findInSocialObj('youtube') ||
        cached.youtube ||
        DEFAULT_SOCIAL_LINKS.youtube,
      tiktok:
        contactObj.tiktok ||
        findInSocialObj('tiktok') ||
        cached.tiktok ||
        DEFAULT_SOCIAL_LINKS.tiktok,
      instagram:
        contactObj.instagram ||
        findInSocialObj('instagram') ||
        cached.instagram ||
        DEFAULT_SOCIAL_LINKS.instagram,
    };

    setCachedSocialLinks(result);
    return result;
  } catch {
    return getCachedSocialLinks();
  }
};

export const saveSocialLinksToDB = async (links: SocialLinksData): Promise<SocialLinksData> => {
  const normalized: SocialLinksData = {
    youtube: normalizeSocialUrl('youtube', links.youtube) || DEFAULT_SOCIAL_LINKS.youtube,
    tiktok: normalizeSocialUrl('tiktok', links.tiktok) || DEFAULT_SOCIAL_LINKS.tiktok,
    instagram: normalizeSocialUrl('instagram', links.instagram) || DEFAULT_SOCIAL_LINKS.instagram,
  };

  setCachedSocialLinks(normalized);

  // 1. Simpan ke profiles.contact JSONB
  try {
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('id, contact')
      .limit(1)
      .maybeSingle();

    if (existingProfile?.id) {
      const updatedContact = {
        ...(existingProfile.contact || {}),
        youtube: normalized.youtube,
        tiktok: normalized.tiktok,
        instagram: normalized.instagram,
      };

      await supabase
        .from('profiles')
        .update({ contact: updatedContact })
        .eq('id', existingProfile.id);
    }
  } catch (e) {
    console.warn('Could not update social links in profiles:', e);
  }

  // 2. Sinkronkan juga ke contact_info.social_links JSONB jika ada
  try {
    const { data: existingContactInfo } = await supabase
      .from('contact_info')
      .select('id, social_links')
      .eq('is_active', true)
      .limit(1)
      .maybeSingle();

    if (existingContactInfo?.id) {
      const updatedSocialLinks = {
        ...(existingContactInfo.social_links || {}),
        YouTube: normalized.youtube,
        TikTok: normalized.tiktok,
        Instagram: normalized.instagram,
      };

      await supabase
        .from('contact_info')
        .update({ social_links: updatedSocialLinks })
        .eq('id', existingContactInfo.id);
    }
  } catch (e) {
    console.warn('Could not update social links in contact_info:', e);
  }

  globalCache.clear();
  return normalized;
};

export const useSocialLinks = () => {
  const [socialLinks, setSocialLinks] = useState<SocialLinksData>(() =>
    getCachedSocialLinks()
  );

  useEffect(() => {
    let mounted = true;
    fetchSocialLinksFromDB().then((data) => {
      if (mounted) setSocialLinks(data);
    });

    const handleStorageUpdate = () => {
      if (mounted) setSocialLinks(getCachedSocialLinks());
    };

    window.addEventListener('social-links-updated', handleStorageUpdate);
    return () => {
      mounted = false;
      window.removeEventListener('social-links-updated', handleStorageUpdate);
    };
  }, []);

  return { socialLinks, setSocialLinks };
};
